import React, {Component} from 'react';
import {Link} from 'react-router-dom';
import teapot from './imgs/teapot-full.png';

class Teapot extends Component {
  render() {
    return (
      <div className="App-body-project">
        <h2>Teapot</h2>
        <h3>Sept. - Oct. 2020</h3>
        <div className="App-body-project-video">
          <iframe title="Teapot Model"
          src="https://www.youtube.com/embed/NtxwapY_4S8" frameBorder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen></iframe>
        </div>
        <h4>Introduction</h4>
        <p>To gain experience with realistic 3D modeling and shading in Maya,
          I decided to model and shade my mom's old red teapot.
        </p>
        <h4>Overview</h4>
        <p>1. Modeling<br />
          2. Shading<br /><br />
          My own personal goals for this project were to gain experience with
          3D modeling and shading in Maya 2020.
        </p>
        <h4>Modeling</h4>
        <p>There are 5 components: the base, lid, handle, and two screw caps
          that connect the handle to the base (one features a holder to keep
          the handle upright). The inside of the teapot base is also modeled.
          Each of these 5 components has its own UV mapping as well.<br /><br />
          There were two main technical challenges that I faced: 1) modeling the
          inside of the teapot base, and 2) modeling the screw caps. The base
          was tricky because I had to figure out how to model similarly shaped
          holes on both the inner and outer shells, and then connect the faucet
          to the cylindrical base such that the faucet also bridged the gap
          between the inner and outer shells. At various points, I ended up with
          extra edges (which appeared as a dark black rim via smooth shading)
          and extra vertices. I eventually figured out how to fix the topology
          by adding the originally intended edge loops and deleting faces such
          that extra edges were removed, and also merging vertices together.
          Modeling the screw caps was difficult because I was unsure how to best
          model (connect) the caps' angled legs to the cylindrical-like cap. In
          my first few attempts, the bridge between the legs and the cap was
          twisted. This made several edges seem too sharp and also made the legs
          look twisted, despite their intended boxy shape. Along the way, I
          figured out that it was best to model the rest of the screw cap first
          before bridging the cap and leg together, to maintain the cap's
          cylindrical-like shape. I later figured out how to make a non-twisted
          version of the bridged legs and cap by adding more edge loops to the
          legs to better match the cap's bridge vertices.<br /><br />
          Aesthetically, my biggest challenge was determining a better intuition
          for modeling curved objects (e.g., the nubs on the screw cap holder).
        </p>
        <h4>Shading</h4>
        <p>There are 3 main types of shaders: a ceramic shader for the red base,
          a rough plastic shader for the handle and joinery, and a metallic
          shader for the screws, washers, and teapot base rim. There are also
          separate shaders for the inside of the teapot (which is a simple
          black iron-like shader) and the bottom of the teapot, which features
          a file texture with lettering (Le Creuset).<br /><br />
          Ceramic shader: I started with an aiStandardSurface shader (ceramic
          preset), and then added a ramp projection to generate the darker color
          gradient around the wide jut of the teapot base. I adjusted the
          diffuse and specular roughness, and also added a coat to make the
          lighting highlights slightly less sharp but still make the ceramic
          material appear glossy. To add wear and tear to this shader, I added
          scratches and dents via a scratch texture bump map and dent texture
          displacement shader, respectively.<br /><br />
          Plastic shader: I started with an aiStandardSurface shader (plastic
          preset). I adjusted the diffuse and specular roughness to make the
          plastic appear more diffuse (less shiny). I also lowered the
          subsurface scattering to make the handle appear darker and more
          consistent with the rest of the teapot. Since the plastic handle and
          joinery are rough to the touch (small bumps), I added some noise via
          a bump map. To add additional realism to this shader, I added
          fingerprints via a texture mapped to the specular roughness (with a
          remapHSV node).<br /><br />
          Metallic shader: I started with an aiStandardSurface shader (metal
          preset). I adjusted the base color, as well as the specular parameters
          and metalness, to make the metallic screw and teapot base rim slightly
          less shiny and reflective.<br /><br />
          The main technical challenge I faced was figuring out how to add
          scratches and dents to the teapot base and lid to make it look
          realistic. I ended up adding dents at specific locations via a
          displacement map that I made in Photoshop that matches up to the UV
          map, which was connected via a file node remapped to a displacement
          shader. For scratches and smaller dents, I eventually figured out how
          to do these via a bump map connected to the normal camera of the
          ceramic shader. This was a similar process to how I added fingerprints
          to the handle and lid knob.<br /><br />
          Aesthetically, the biggest challenge I had was getting the plastic
          material to be dark enough to match the teapot. The plastic shader had
          so many highlights that it didn't look like it was really attached to
          the teapot. In order to get the plastic and ceramic shaders to better
          match, I noticed that the black plastic shader was much more diffuse
          than the plastic preset. Ultimately, to get the plastic shader to
          appear dark enough to be consistent with the ceramic shader, I had to
          lower the strength of the subsurface scattering and the subsurface
          scattering radius.
        </p>
        <h4>Reflection & Future Work</h4>
        <p>My model can be improved by continuing to refine the screw
          caps, as several of the quad faces appear curved on smooth mode. In
          addition, the back bottom edge could be further refined. The bottoms
          of the caps could also be modeled in finer detail, as there are metal
          brackets attached to the teapot underneath the cap covers; however,
          these are only somewhat visible when looking at the physical teapot up
          close from a low angle.<br /><br />
          The shading can be further improved by adding discoloration to some
          of the bigger dents in the teapot body (as if the coat was chipping
          off or melted off). These are the dents modeled in the displacement
          shader component of the ceramic shading group. In addition, while I
          did shade the bottom of the teapot (not shown in the turntable
          animation), it could be further refined. In particular, there seems to
          be a sharp colored edge between the bottom and the side in certain
          areas at the UV seam; this could be smoothed out. The iron shader for
          the inside of the teapot could also be further refined, if this was
          used as a hero object (the inside is already modeled).
        </p>
        <input type="image" src={teapot} alt="Teapot"/>
        <div className="App-body-project-buttons">
          <Link to="/lechat">
            <button type="button">
              &#8249; Previous: Le Chat
            </button>
          </Link>
          <Link to="/work">
            <button type="button">
              Back to Portfolio
            </button>
          </Link>
          <Link to="/tiletunes">
            <button type="button">
              Next: Tile Tunes &#8250;
            </button>
          </Link>
        </div>
      </div>
    );
  }
}

export default Teapot;
